import React, { useCallback, useEffect, useState } from 'react';
import { INotification } from 'stores/networkInterfaces';
import { INotificationQuery } from 'stores/NotificationStore';
import { useStore } from 'stores/store';
import { isTextSelection } from 'utils/domUtils';
import { useUserAuth } from 'utils/hooks/useUserAuth';

export const useWatchNotificationApi = ({ unread }: { unread?: boolean }) => {
  const store = useStore();
  const { isAuthenticated, isManagement } = useUserAuth();

  const [query, setQuery] = useState<INotificationQuery>({
    limit: 10,
    offset: 0,
    filter: unread ? 'unread' : undefined
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && !isManagement) {
      if (isLoading) {
        store.NotificationStore.listWatchNotifications(query)
          .catch((e) => {
            console.log('Failed to fetch watch notifications', e);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [isAuthenticated, isManagement, isLoading, query, store.NotificationStore]);

  const setUnread = useCallback(
    (unread?: boolean) => {
      const newFilter = unread ? 'unread' : undefined;
      if (newFilter != query?.filter) {
        setQuery({
          limit: 10,
          offset: 0,
          filter: newFilter
        });
        setIsLoading(true);
      }
    },
    [query?.filter]
  );

  const onLoadMore = useCallback(() => {
    setQuery({
      limit: 10,
      offset: unread
        ? Math.min(store.NotificationStore?.unreadWatchCount, (query?.offset || 0) + 10)
        : Math.min(store.NotificationStore?.totalWatchCount, (query?.offset || 0) + 10),
      filter: query?.filter
    });
    setIsLoading(true);
  }, [
    query?.filter,
    query?.offset,
    store.NotificationStore?.totalWatchCount,
    store.NotificationStore?.unreadWatchCount,
    unread
  ]);

  const onRefresh = useCallback(() => {
    setQuery({
      limit: 10,
      offset: 0,
      filter: query?.filter
    });
    setIsLoading(true);
  }, [query?.filter]);

  const onToggleUnread = useCallback(
    (notification: INotification) => {
      if (!isTextSelection()) {
        if (notification.read_at) {
          store.NotificationStore.markWatchAsUnread(notification);
        } else {
          store.NotificationStore.markWatchAsRead(notification);
        }
      }
    },
    [store.NotificationStore]
  );

  const onNavigate = useCallback(
    async (notification: INotification) => {
      if (!isTextSelection()) {
        store.NotificationStore.markWatchAsRead(notification);
        await store.NotificationStore.navigateToNotification(notification);
        store.UIStore.setNotificationsActive(false);
      }
    },
    [store.NotificationStore, store.UIStore]
  );

  const setReadAll = useCallback(async () => {
    await store.NotificationStore.markAllWatchAsRead();
  }, [store.NotificationStore]);

  return {
    isAuthenticated,
    isLoading,
    setUnread,
    onRefresh,
    onLoadMore,
    onToggleUnread,
    setReadAll,
    onNavigate
  };
};
